import React, { useState, useEffect, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import nunjucks from 'nunjucks';

// Configure nunjucks with custom delimiters
nunjucks.configure({
  autoescape: false,
  tags: {
    variableStart: '${{',
    variableEnd: '}}',
  },
});

export const LoadBalancerField = (
  props: FieldExtensionComponentProps<string>
) => {
  const { onChange, formData, required, rawErrors, schema, uiSchema, formContext } = props;
  
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  // Get type from ui:options (project or product)
  const type = uiSchema?.['ui:options']?.type || 'project';
  
  // Get project name from ui:options
  const projectNameTemplate = uiSchema?.['ui:options']?.projectName || '';
  const resolvedProjectName = useMemo(() => {
    if (!projectNameTemplate) return '';
    return nunjucks.renderString(projectNameTemplate, formContext.formData || {}).trim();
  }, [projectNameTemplate, formContext.formData]);

  // Normalize the incoming formData: if it's undefined or an empty string, force it to "false".
  const normalized = formData && formData.trim() !== '' ? formData : 'false';

  // Determine the initial checkbox state and radio value
  const initialChecked = normalized !== 'false';
  const initialRadio = initialChecked ? normalized : '';

  // Local state for the checkbox and radio.
  const [checked, setChecked] = useState<boolean>(initialChecked);
  const [radio, setRadio] = useState<string>(initialRadio);
  
  // State for project load balancer check
  const [loading, setLoading] = useState<boolean>(false);
  const [projectHasShared, setProjectHasShared] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Fetch project details if type is "product"
  useEffect(() => {
    if (type !== 'product' || !resolvedProjectName) {
      setProjectHasShared(true); // Default to true for project type
      return;
    }

    const fetchProjectDetails = async () => {
      setLoading(true);
      setFetchError(null);
      
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        
        const response = await fetch(`${baseUrl}/aws/params`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ name: `/backstage/project/${resolvedProjectName}` }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch project details: ${response.status}`);
        }

        const data = await response.json();
        const rawValue = data.value || '';
        
        if (rawValue) {
          const parsed = yaml.load(rawValue);
          if (parsed && typeof parsed === 'object') {
            const components = (parsed as any).components || {};
            const loadBalancer = components.load_balancer || 'false';
            
            // Check if project has shared load balancer
            const hasShared = loadBalancer === 'shared';
            setProjectHasShared(hasShared);
            
            console.log('LoadBalancer: Project load_balancer config:', loadBalancer);
            console.log('LoadBalancer: Project has shared:', hasShared);
          }
        }
      } catch (error: any) {
        console.error('LoadBalancer: Error fetching project details:', error);
        setFetchError(error.message);
        setProjectHasShared(false); // Default to false on error
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, [type, resolvedProjectName, discoveryApi, identityApi]);

  // On mount, if the parent value is empty, force it to "false".
  useEffect(() => {
    if (!formData || formData.trim() === '') {
      // Use a timeout to ensure the onChange call happens after mount.
      setTimeout(() => {
        onChange('false');
      }, 0);
    }
  }, [formData, onChange]);

  // Whenever formData changes, update local state.
  useEffect(() => {
    const val = formData && formData.trim() !== '' ? formData : 'false';
    if (val === 'false') {
      setChecked(false);
      setRadio('');
    } else {
      setChecked(true);
      setRadio(val as 'dedicated' | 'shared');
    }
  }, [formData]);

  // Retrieve labels from the schema and uiSchema.
  const checkboxLabel = schema.title || 'LoadBalancer required';
  const helperText = schema.description || '';
  const radioGroupLabel =
    typeof uiSchema?.['ui:options']?.radioGroupLabel === 'string'
      ? uiSchema['ui:options'].radioGroupLabel
      : 'Select LoadBalancer Type';

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = event.target.checked;
    setChecked(newChecked);
    if (newChecked) {
      // On checking, default to "shared" if available, otherwise "dedicated"
      const isSharedAvailable = showSharedOption;
      const newRadio = radio || (isSharedAvailable ? 'shared' : 'dedicated');
      setRadio(newRadio);
      onChange(newRadio);
    } else {
      // On unchecking, clear radio and set value to "false".
      setRadio('');
      onChange('false');
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRadio = event.target.value as 'dedicated' | 'shared';
    setRadio(newRadio);
    onChange(newRadio);
  };

  // Determine if shared option should be shown
  const showSharedOption = type === 'project' || (type === 'product' && projectHasShared);

  if (loading) {
    return (
      <FormControl component="fieldset" fullWidth>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={checked} disabled />}
            label={checkboxLabel}
          />
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
            <CircularProgress size={20} style={{ marginRight: '8px' }} />
            <span>Checking project configuration...</span>
          </div>
        </FormGroup>
      </FormControl>
    );
  }

  return (
    <FormControl
      component="fieldset"
      fullWidth
      required={required}
      error={!!rawErrors?.length}
    >
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={handleCheckboxChange} />}
          label={checkboxLabel}
        />
      </FormGroup>
      {checked && (
        <div style={{ marginLeft: '20px' }}>
          <FormLabel component="legend">{radioGroupLabel}</FormLabel>
          <RadioGroup value={radio} onChange={handleRadioChange}>
            {showSharedOption && (
              <>
                <FormControlLabel
                  value="shared"
                  control={<Radio />}
                  label="Shared LoadBalancer (recommended)"
                />
                <FormHelperText style={{ marginLeft: '32px', marginTop: '-4px', marginBottom: '8px' }}>
                  Share the project's load balancer with other products (cost-effective)
                </FormHelperText>
              </>
            )}
            <FormControlLabel
              value="dedicated"
              control={<Radio />}
              label="Dedicated LoadBalancer"
            />
            <FormHelperText style={{ marginLeft: '32px', marginTop: '-4px', marginBottom: '8px' }}>
              Separate load balancer for this product only (isolated traffic, custom SSL certs)
            </FormHelperText>
          </RadioGroup>
          {type === 'product' && !projectHasShared && (
            <FormHelperText>
              Shared option is not available because the project does not have a shared load balancer configured.
            </FormHelperText>
          )}
          {fetchError && (
            <FormHelperText error>
              Warning: Could not verify project configuration. {fetchError}
            </FormHelperText>
          )}
        </div>
      )}
      <FormHelperText>
        {rawErrors && rawErrors.length > 0 ? rawErrors.join(', ') : helperText}
      </FormHelperText>
    </FormControl>
  );
};
